class YouTubeAPI {
    static readyPromise = null;

    static load() {
        if (typeof window === 'undefined') return Promise.resolve();

        if (window.YT && window.YT.Player) return Promise.resolve();

        if (!this.readyPromise) {
            this.readyPromise = new Promise((resolve) => {
                const scriptId = 'yt-iframe-api';
                if (!document.getElementById(scriptId)) {
                    const tag = document.createElement('script');
                    tag.id = scriptId;
                    tag.src = 'https://www.youtube.com/iframe_api';
                    document.head.appendChild(tag);
                }

                const prev = window.onYouTubeIframeAPIReady;
                window.onYouTubeIframeAPIReady = () => {
                    if (typeof prev === 'function') prev();
                    resolve();
                };
            });
        }
        return this.readyPromise;
    }
}

class YouTubePlayerHelper {
    /**
     * Map<elementId, YT.Player>
     */
    players = new Map();

    /**
     * Map<elementId, Promise<void>> - resolve quando onReady dispara
     */
    readyPromises = new Map();

    /**
     * Cria (se preciso) e retorna um player para o alvo.
     * @param {HTMLElement|string} target elemento <div> ou id do container do player
     * @param {{ videoId?: string, onStateChange?: Function, playerVars?: Object }} [opts]
     * @returns {Promise<YT.Player|null>}
     */
    async ensure(target, { videoId, onStateChange, playerVars } = {}) {
        await YouTubeAPI.load();

        const { el, id } = this.#resolveTarget(target);
        if (!el || !id) return null;

        const existing = this.players.get(id);
        if (existing) return existing;

        const vid = videoId || el.dataset.youtubeId;
        if (!vid) return null;

        let resolveReady;
        const readyPromise = new Promise((res) => (resolveReady = res));
        this.readyPromises.set(id, readyPromise);

        const finalPlayerVars = {
            ...this.#getDefaultPlayerVars(),
            ...(playerVars || {}),
        };

        const player = new YT.Player(id, {
            height: '360',
            width: '640',
            videoId: vid,
            playerVars: finalPlayerVars,
            events: {
                onReady: () => resolveReady(),
                onStateChange: onStateChange,
            },
        });

        this.players.set(id, player);
        return player;
    }

    /**
     * Monta explicitamente um player (caso prefira não depender do play "preguiçoso").
     * @param {{ videoId: string, elementId?: string, element?: HTMLElement, onStateChange?: Function, playerVars?: Object }} opts
     * @returns {Promise<YT.Player|null>}
     */
    async mount({ videoId, elementId, element, onStateChange, playerVars } = {}) {
        await YouTubeAPI.load();

        // aceita elementId OU element
        const target = element || elementId;
        const { el } = this.#resolveTarget(target, { allowCreateIdFromData: true });
        if (!el) return null;

        return this.ensure(el, { videoId, onStateChange, playerVars });
    }

    /**
     * Reproduz o vídeo (cria/monta o player se não existir).
     * @param {HTMLElement|string} target
     * @param {{ muteIfBlocked?: boolean, videoId?: string, onStateChange?: Function, playerVars?: Object }} [options]
     */
    async play(target, { muteIfBlocked = true, videoId, onStateChange, playerVars } = {}) {
        const { id } = this.#resolveTarget(target);
        let player = id ? this.players.get(id) : null;

        if (!player) {
            player = await this.ensure(target, { videoId, onStateChange, playerVars });
            if (!player) return;
        }

        const ready = this.readyPromises.get(this.#playerId(player));
        if (ready) await ready;

        try {
            player.playVideo();

            await this.#sleep(300);
            const state = player.getPlayerState?.();
            const isPlaying = state === YT.PlayerState.PLAYING;

            if (!isPlaying && muteIfBlocked) {
                if (typeof player.mute === 'function') player.mute();
                player.playVideo();
                await this.#sleep(300);
            }
        } catch {
            // falha silenciosa (autoplay bloqueado, etc.)
        }
    }


    /**
     * Pausa um player específico (se existir).
     * @param {HTMLElement|string} target
     */
    async pause(target) {
        const { id } = this.#resolveTarget(target);
        if (!id) return;
        const p = this.players.get(id);
        if (!p) return;

        const ready = this.readyPromises.get(id);
        if (ready) await ready;

        if (typeof p.pauseVideo === 'function') p.pauseVideo();
    }

    /**
     * Pausa todos os players, exceto o informado.
     * @param {string} exceptId
     */
    async pauseOthers(exceptId) {
        for (const [id, p] of this.players.entries()) {
            if (id === exceptId) continue;
            try {
                const ready = this.readyPromises.get(id);
                if (ready) await ready;
                if (typeof p.pauseVideo === 'function') p.pauseVideo();
            } catch { }
        }
    }

    /**
     * Pausa todos os players.
     */
    async pauseAll() {
        for (const [id] of this.players.entries()) {
            await this.pause(id);
        }
    }

    /**
     * Destroi um player específico e limpa seus registros.
     * @param {string} elementId
     */
    destroy(elementId) {
        const p = this.players.get(elementId);
        if (p && typeof p.destroy === 'function') p.destroy();
        this.players.delete(elementId);
        this.readyPromises.delete(elementId);
    }

    /**
     * Destroi todos os players.
     */
    destroyAll() {
        for (const [id, p] of this.players.entries()) {
            try {
                if (typeof p.destroy === 'function') p.destroy();
            } catch { }
        }
        this.players.clear();
        this.readyPromises.clear();
    }

    /**
     * Retorna uma instância já criada (ou undefined).
     * @param {string} elementId
     */
    get(elementId) {
        return this.players.get(elementId);
    }

    /**
     * Diz se o player (id) já disparou onReady.
     * @param {string} elementId
     */
    async isReady(elementId) {
        const ready = this.readyPromises.get(elementId);
        if (!ready) return !!this.players.get(elementId);
        try {
            await ready;
            return true;
        } catch {
            return false;
        }
    }

    /**
     * Espera o onReady para o player (id).
     * @param {string} elementId
     * @returns {Promise<void>}
     */
    whenReady(elementId) {
        const ready = this.readyPromises.get(elementId);
        return ready || Promise.resolve();
    }

    // =============== privados utilitários ===============

    #sleep(ms) {
        return new Promise((r) => setTimeout(r, ms));
    }

    #playerId(player) {
        return player?.getIframe?.()?.id;
    }

    /**
     * PlayerVars padrão (podem ser sobrescritos via playerVars customizado)
     */
    #getDefaultPlayerVars() {
        return {
            rel: 0,
            showinfo: 0,
            controls: 0,
            modestbranding: 1,
            disablekb: 1,
            fs: 0,
            playsinline: 1,
            loop: 1,
            origin: window.location.origin,
        };
    }

    /**
     * Resolve target para { el, id }. Se não houver id:
     * - usa el.id se existir;
     * - senão, cria a partir de data-youtube-id;
     * - como fallback, gera um id único.
     */
    #resolveTarget(target, { allowCreateIdFromData = true } = {}) {
        if (typeof window === 'undefined') return { el: null, id: null };

        let el = null;
        if (typeof target === 'string') {
            el = document.getElementById(target);
        } else if (target && target.nodeType === 1) {
            el = target;
        }

        if (!el) return { el: null, id: null };

        if (el.id && el.id.trim() !== '') {
            return { el, id: el.id };
        }

        if (allowCreateIdFromData && el.dataset?.youtubeId) {
            el.id = `yt-${el.dataset.youtubeId}`;
            return { el, id: el.id };
        }

        el.id = `yt-el-${Math.random().toString(36).slice(2, 9)}`;
        return { el, id: el.id };
    }
}

const YouTubePlayer = new YouTubePlayerHelper();
export default YouTubePlayer;
export { YouTubeAPI, YouTubePlayerHelper };
